import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { getAuthToken } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, EyeIcon, TrashIcon, XMarkIcon, ArrowUpTrayIcon, SparklesIcon } from '@heroicons/react/24/outline';
import AddLeadModal from './AddLeadModal';
import BulkImageImportModal from './BulkImageImportModal';



interface Lead {
  leadId: string;
  branchId: string;
  parentName: string;
  parentEmail: string;
  parentPhone: string;
  studentName: string;
  studentAge: number;
  currentSchool?: string;
  preferredGrade: string;
  inquirySource: string;
  stage: string;
  priority: string;
  leadScore: number;
  lead_points?: number;
  team_id?: string;
  referral_type?: string;
  assignedTo?: string;
  source?: string;
  createdAt: string;
  communications?: any[];
}

interface LeadManagementProps {
  branchId?: string;
}

const LeadManagement: React.FC<LeadManagementProps> = ({ branchId = 'kolkata' }) => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkImageModal, setShowBulkImageModal] = useState(false);


  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStage, setSelectedStage] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [leadToArchive, setLeadToArchive] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'archive' | 'permanent' | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [leadSummary, setLeadSummary] = useState({ hot: 0, admitted: 0, avgPoints: 0 });

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedStage('all');
    setSelectedPriority('all');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const computeLeadSummary = (rows: Lead[]) => {
    const isAdmitted = (stage: string) => {
      const s = (stage || '').toLowerCase();
      return s === 'admitted' || s === 'enrolled';
    };
    const pointsSum = rows.reduce((sum, l) => sum + (l.lead_points ?? l.leadScore ?? 0), 0);
    return {
      hot: rows.filter((l) => (l.priority || '').toLowerCase() === 'hot').length,
      admitted: rows.filter((l) => isAdmitted(l.stage)).length,
      avgPoints: rows.length > 0 ? Math.round(pointsSum / rows.length) : 0,
    };
  };

  const getTeamColor = (teamId: string) => {
    const colors: { [key: string]: string } = {
      blue: '#2563eb',
      green: '#16a34a',
      red: '#dc2626',
      yellow: '#ca8a04',
      orange: '#ea580c'
    };
    return colors[teamId] || '#6b7280';
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchLeads(1);
  }, [branchId, selectedStage, selectedPriority, dateFrom, dateTo]);

  const fetchLeads = async (page: number = 1, limit: number = pageSize) => {
    try {
      setLoading(true);
      const token = await getAuthToken();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(branchId !== 'all' && { branchId }),
        ...(selectedStage !== 'all' && { stage: selectedStage }),
        ...(selectedPriority !== 'all' && { priority: selectedPriority }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo })
      });
      
      const url = `${API_BASE_URL}/leads?${params}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setLeads(data.data);
        if (data.summary) {
          setLeadSummary(data.summary);
        } else if (data.pagination && data.pagination.totalRecords > data.data.length) {
          const statsParams = new URLSearchParams({
            page: '1',
            limit: Math.min(data.pagination.totalRecords, 500).toString(),
            ...(branchId !== 'all' && { branchId }),
            ...(selectedStage !== 'all' && { stage: selectedStage }),
            ...(selectedPriority !== 'all' && { priority: selectedPriority }),
            ...(dateFrom && { dateFrom }),
            ...(dateTo && { dateTo }),
          });
          const statsRes = await fetch(`${API_BASE_URL}/leads?${statsParams}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const statsData = await statsRes.json();
          if (statsData.success) {
            setLeadSummary(computeLeadSummary(statsData.data));
          }
        } else {
          setLeadSummary(computeLeadSummary(data.data));
        }
        if (data.pagination) {
          setTotalPages(data.pagination.totalPages);
          setTotalRecords(data.pagination.totalRecords);
          setCurrentPage(data.pagination.currentPage);
        }
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLeadAdded = () => {
    fetchLeads(1);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    void fetchLeads(1, newSize);
  };

  const handleArchiveLead = async (leadId: string) => {
    try {
      setArchiving(true);
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/leads/${leadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          permanent: deleteMode === 'permanent'
        })
      });

      const data = await response.json();
      if (data.success) {
        // Remove from list
        setLeads(leads.filter(lead => lead.leadId !== leadId));
        setLeadToArchive(null);
        setDeleteMode(null);
        // Refresh to maintain pagination
        fetchLeads(currentPage);
      } else {
        alert('Failed to delete lead');
      }
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Error deleting lead');
    } finally {
      setArchiving(false);
    }
  };

  const stages = [
    { value: 'all', label: 'All Stages' },
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'tour_scheduled', label: 'Tour Sched.' },
    { value: 'tour_completed', label: 'Tour Compl.' },
    { value: 'application_submitted', label: 'Applicatio.' },
    { value: 'admitted', label: 'Admitted' },
    { value: 'lost', label: 'Lost' }
  ];

  const priorities = [
    { value: 'all', label: 'All Priorities' },
    { value: 'hot', label: 'Hot' },
    { value: 'warm', label: 'Warm' },
    { value: 'cold', label: 'Cold' }
  ];

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'hot': return { badge: 'badge-hot', icon: '🔥' };
      case 'warm': return { badge: 'badge-warm', icon: '☀️' };
      case 'cold': return { badge: 'badge-cold', icon: '❄️' };
      default: return { badge: 'badge-warm', icon: '☀️' };
    }
  };

  const getStatusBadge = (stage: string) => {
    switch (stage) {
      case 'new': return 'status-new';
      case 'contacted': return 'status-contacted';
      case 'qualified': return 'status-contacted';
      case 'tour_scheduled': return 'status-scheduled';
      case 'tour_completed': return 'status-scheduled';
      case 'application_submitted': return 'status-applied';
      case 'admitted': return 'status-admitted';
      case 'enrolled': return 'status-admitted';
      case 'lost': return 'status-rejected';
      default: return 'status-new';
    }
  };

  const getLeadScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  // Client-side search filtering
  const filteredLeads = leads.filter(lead => {
    const searchLower = searchTerm.toLowerCase();
    return (
      lead.parentName.toLowerCase().includes(searchLower) ||
      lead.studentName.toLowerCase().includes(searchLower) ||
      lead.parentEmail.toLowerCase().includes(searchLower) ||
      lead.parentPhone.includes(searchTerm) ||
      lead.preferredGrade.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lead Management</h1>
          <p className="text-gray-600">Manage and track prospective students</p>
        </div>
        <div className="flex gap-3">

          <button 
            onClick={() => setShowBulkImageModal(true)}
            className="inline-flex items-center px-4 py-2 border border-blue-200 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-all"
          >
            <SparklesIcon className="h-5 w-5 mr-2" />
            Bulk AI Import
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add New Lead
          </button>
        </div>
      </div>



      <BulkImageImportModal
        isOpen={showBulkImageModal}
        onClose={() => setShowBulkImageModal(false)}
        onUploadSuccess={() => fetchLeads(1)}
        branchId={branchId}
      />

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onLeadAdded={handleLeadAdded}
        branchId={branchId}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="glass-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Leads</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? <span className="text-gray-300 animate-pulse text-xl">...</span> : totalRecords}
              </p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="glass-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Hot Leads</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? <span className="text-gray-300 animate-pulse text-xl">...</span> : leadSummary.hot}
              </p>
            </div>
            <div className="p-3 bg-rose-100 rounded-lg">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="glass-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Admitted</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? <span className="text-gray-300 animate-pulse text-xl">...</span> : leadSummary.admitted}
              </p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="glass-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Points</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? <span className="text-gray-300 animate-pulse text-xl">...</span> : leadSummary.avgPoints}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
        </div>
      </div>


      {/* Leads Table */}
      <div className="glass-card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Lead Info
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Grade
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">
                  Pri 
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Team
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Stage
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
              {/* Filter Row */}
              <tr className="bg-white border-t border-gray-200">
                <th className="px-3 py-1.5">
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-3 w-3 absolute left-2 top-1.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input-field pl-7 text-[10px] py-0.5 h-7"
                    />
                  </div>
                </th>
                <th className="px-3 py-1.5">
                  <select className="input-field text-[10px] py-0.5" onChange={(e) => {
                    // Grade filter can be added here
                  }}>
                    <option value="">All</option>
                  </select>
                </th>
                <th className="px-3 py-1.5"></th>
                <th className="px-3 py-1.5">
                  <select 
                    value={selectedPriority} 
                    onChange={(e) => setSelectedPriority(e.target.value)}
                    className="input-field text-[10px] py-0.5"
                  >
                    {priorities.map((priority) => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label.slice(0,3)}
                      </option>
                    ))}
                  </select>
                </th>
                <th className="px-3 py-1.5"></th>
                <th className="px-3 py-1.5">
                  <select 
                    value={selectedStage} 
                    onChange={(e) => setSelectedStage(e.target.value)}
                    className="input-field text-[10px] py-0.5"
                  >
                    {stages.map((stage) => (
                      <option key={stage.value} value={stage.value}>
                        {stage.label.length > 10 ? stage.label.slice(0, 10) + '.' : stage.label}
                      </option>
                    ))}
                  </select>
                </th>
                <th className="px-3 py-1.5"></th>
                <th className="px-3 py-1.5 min-w-[180px]">
                  <div className="flex items-center gap-1">
                    <input 
                      type="date" 
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="input-field text-[10px] py-0.5 h-7 w-28"
                      title="From date"
                    />
                    <span className="text-gray-300">-</span>
                    <input 
                      type="date" 
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="input-field text-[10px] py-0.5 h-7 w-28"
                      title="To date"
                    />
                  </div>
                </th>
                <th className="px-3 py-1.5 flex justify-center">
                  {(searchTerm || selectedStage !== 'all' || selectedPriority !== 'all' || dateFrom || dateTo) && (
                    <button 
                      onClick={resetFilters}
                      className="text-[10px] text-red-600 hover:text-red-800 font-bold bg-red-50 px-2 py-0.5 rounded border border-red-200 transition-all flex items-center gap-1 group"
                      title="Clear all filters"
                    >
                      <XMarkIcon className="h-3 w-3 group-hover:rotate-90 transition-transform" /> RESET
                    </button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                    Loading leads...
                  </td>
                </tr>
              ) : filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                    No leads found
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead) => {
                  const priorityInfo = getPriorityBadge(lead.priority);
                  return (
                    <tr key={lead.leadId} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="leading-tight">
                          <div className="text-sm font-bold text-gray-900 truncate max-w-[150px]">{lead.studentName} ({lead.studentAge}y)</div>
                          <div className="text-xs text-gray-500 truncate max-w-[150px]">{lead.parentName}</div>
                          <div className="flex items-center gap-2 mt-1 opacity-70 group-hover:opacity-100 transition-opacity">
                            <span className="text-[10px] text-gray-400 font-medium truncate">{lead.parentPhone}</span>
                            {lead.parentEmail && (
                              <>
                                <span className="text-[10px] text-gray-300">•</span>
                                <span className="text-[10px] text-gray-400 truncate max-w-[100px]" title={lead.parentEmail}>
                                  {lead.parentEmail.split('@')[0]}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700 font-medium">
                        {lead.preferredGrade}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="px-2 py-0.5 inline-flex text-[10px] leading-4 font-semibold rounded-md bg-blue-50 text-blue-700 border border-blue-100">
                          {lead.source || 'Direct'}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`${priorityInfo.badge} px-2 py-0.5 text-[10px] flex items-center gap-1 w-fit`} title={lead.priority.toUpperCase()}>
                          {priorityInfo.icon} <span className="uppercase">{lead.priority}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {lead.team_id ? (
                          <span className="px-2 py-0.5 inline-flex text-[10px] leading-4 font-bold rounded-md text-white"
                            style={{ backgroundColor: getTeamColor(lead.team_id) }}>
                            {lead.team_id.toUpperCase()}
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-300">No Team</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`${getStatusBadge(lead.stage)} px-2 py-0.5 text-[10px] font-bold`}>
                          {lead.stage.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md border ${getLeadScoreColor(lead.leadScore)}`}>
                          {lead.lead_points !== undefined ? lead.lead_points : lead.leadScore} pts
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-500 font-medium">
                        {lead.createdAt ? (() => {
                          try {
                            let date;
                            const createdAt = lead.createdAt as any;
                            if (typeof createdAt === 'object' && createdAt._seconds) {
                              date = new Date(createdAt._seconds * 1000);
                            } else if (typeof createdAt === 'string') {
                              date = new Date(createdAt);
                            }
                            if (date && !isNaN(date.getTime())) {
                              return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
                            }
                          } catch (e) {
                            console.error('Date parsing error:', e);
                          }
                          return 'N/A';
                        })() : 'N/A'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => navigate(`/leads/${lead.leadId}`)}
                            className="bg-gray-100 p-1.5 rounded-lg text-gray-600 hover:bg-gray-200"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => setLeadToArchive(lead.leadId)}
                            className="bg-red-50 p-1.5 rounded-lg text-red-500 hover:bg-red-100"
                            title="Archive Lead"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination — always show when there are leads so page-size control stays available */}
        {totalRecords > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} leads
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Rows per page:</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              {totalPages > 1 && (
                <>
              <button
                onClick={() => fetchLeads(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const startPage = Math.max(1, currentPage - 2);
                return startPage + i;
              }).filter((page) => page <= totalPages).map((page) => (
                <button
                  key={page}
                  onClick={() => fetchLeads(page)}
                  className={`px-3 py-1 text-sm rounded-md ${
                    currentPage === page
                      ? 'bg-primary-600 text-white'
                      : 'border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span className="px-2 py-1">...</span>
                  <button
                    onClick={() => fetchLeads(totalPages)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                onClick={() => fetchLeads(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {leadToArchive && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl p-6 m-4 max-w-md">
            {!deleteMode ? (
              <>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Lead</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Choose how you want to delete this lead:
                </p>
                <div className="space-y-3 mb-6">
                  <button
                    onClick={() => setDeleteMode('archive')}
                    className="w-full px-4 py-3 text-left border border-yellow-300 rounded-lg hover:bg-yellow-50 transition"
                  >
                    <p className="font-semibold text-yellow-900">📦 Archive Lead</p>
                    <p className="text-xs text-yellow-700 mt-1">Soft delete - can be restored later</p>
                  </button>
                  <button
                    onClick={() => setDeleteMode('permanent')}
                    className="w-full px-4 py-3 text-left border border-red-300 rounded-lg hover:bg-red-50 transition"
                  >
                    <p className="font-semibold text-red-900">🗑️ Permanently Delete</p>
                    <p className="text-xs text-red-700 mt-1">Hard delete - cannot be recovered</p>
                  </button>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setLeadToArchive(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {deleteMode === 'archive' ? 'Archive Lead?' : 'Permanently Delete Lead?'}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {deleteMode === 'archive' 
                    ? 'This lead will be archived and can be restored later if needed.'
                    : '⚠️ WARNING: This will permanently delete the lead from the system. This action cannot be undone!'}
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setDeleteMode(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={archiving}
                  >
                    Back
                  </button>
                  <button
                    onClick={() => leadToArchive && handleArchiveLead(leadToArchive)}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      deleteMode === 'archive'
                        ? 'bg-yellow-600 hover:bg-yellow-700'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                    disabled={archiving}
                  >
                    {archiving ? 'Deleting...' : deleteMode === 'archive' ? 'Archive Lead' : 'Permanently Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManagement;
